import { useCallback, useEffect, useRef, useState } from 'react';
import { useVisualizerStore } from '@/stores/useVisualizerStore';
import { isEmbedded } from '@/utils/config';
import { getHostHeight, onHostHeightChange } from '@/embed/postMessage';
import { CanvasViewer } from '@/components/CanvasViewer';

const IS_EMBED = isEmbedded();

/**
 * The Preview screen's canvas fills available height (flex-1), so in embed flow
 * mode it needs an explicit height. We base it on the *host* viewport (reported
 * via postMessage) so the image viewer adapts to the device instead of
 * collapsing. Falls back to a sensible fixed height before the handshake lands.
 */
function useEmbedPreviewHeight(): number | null {
  const [hostHeight, setHostHeight] = useState<number | null>(getHostHeight());
  useEffect(() => {
    if (!IS_EMBED) return;
    return onHostHeightChange(setHostHeight);
  }, []);
  if (!IS_EMBED) return null;
  const basis = hostHeight ?? (typeof window !== 'undefined' ? window.innerHeight : 720);
  return Math.round(Math.min(760, Math.max(440, basis * 0.85)));
}
import { ColorSwatches } from '@/components/ColorSwatches';
import { GlossSelector } from '@/components/GlossSelector';
import { BeforeAfterSlider } from '@/components/BeforeAfterSlider';
import { QuoteRequestModal } from '@/components/QuoteRequestModal';
import { reportQuoteRequested, reportFinishSelected } from '@/embed/postMessage';
import { showToast } from '@/components/Toasts';
import { usePreviewJob } from '@/hooks/usePreviewJob';
import { addWatermark } from '@/utils/watermark';
import { submitQuoteRequest } from '@/api/quote';
import { useTenant } from '@/utils/tenant';

export function PreviewScreen() {
  const uploadedImage = useVisualizerStore((s) => s.uploadedImage);
  const selectedFinish = useVisualizerStore((s) => s.selectedFinish);
  const selectedGloss = useVisualizerStore((s) => s.selectedGloss);
  const beforeAfterRatio = useVisualizerStore((s) => s.beforeAfterRatio);
  const setBeforeAfterRatio = useVisualizerStore((s) => s.setBeforeAfterRatio);
  const compositeDataUrl = useVisualizerStore((s) => s.compositeDataUrl);
  const previewResultUrl = useVisualizerStore((s) => s.previewResultUrl);
  const userEmail = useVisualizerStore((s) => s.userEmail);
  const userName = useVisualizerStore((s) => s.userName);
  const userPhone = useVisualizerStore((s) => s.userPhone);
  const userZip = useVisualizerStore((s) => s.userZip);
  const setUserContact = useVisualizerStore((s) => s.setUserContact);
  const beforeImagePublicUrl = useVisualizerStore((s) => s.beforeImagePublicUrl);
  const { slug: tenantSlug, businessName, website, phone, quoteRecipientEmail } = useTenant();

  const [showBeforeAfter, setShowBeforeAfter] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'save' | 'quote' | null>(null);
  const [compareHighlighted, setCompareHighlighted] = useState(false);
  const hasMountedRef = useRef(false);
  const hasHighlightedCompareRef = useRef(false);

  const { submit: submitPreviewJob } = usePreviewJob();

  // When finish or gloss changes, regenerate
  useEffect(() => {
    if (!previewResultUrl || isRegenerating) return;

    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    const timer = setTimeout(() => {
      setIsRegenerating(true);
      submitPreviewJob().finally(() => setIsRegenerating(false));
    }, 400);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFinish.id, selectedGloss]);

  const [isSaving, setIsSaving] = useState(false);

  // Both Save and Quote require the same contact info (name, phone, email, zip).
  // hasContactInfo mirrors the validation enforced by QuoteRequestModal.
  const hasContactInfo =
    !!userName &&
    userName.trim().length >= 2 &&
    !!userPhone &&
    userPhone.trim().length >= 7 &&
    !!userEmail &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail) &&
    !!userZip &&
    /^\d{5}(-\d{4})?$/.test(userZip.trim());

  // Send the lead to BSS exactly like a quote (before/after photos + contact info).
  const sendQuote = useCallback(
    async (data: { name: string; phone: string; email: string; zipCode: string }) => {
      const beforeUrl = beforeImagePublicUrl || uploadedImage?.sourceUrl || '';
      const afterUrl = previewResultUrl || compositeDataUrl || '';

      if (!beforeUrl || !afterUrl) {
        showToast('Missing image data. Please try again.', 'error');
        throw new Error('Missing image URLs');
      }

      await submitQuoteRequest({
        name: data.name,
        email: data.email,
        phone: data.phone,
        zipCode: data.zipCode,
        beforeImageUrl: beforeUrl,
        afterImageUrl: afterUrl,
        finishId: selectedFinish.id,
        glossLevel: selectedGloss,
        tenantSlug,
      });

      reportQuoteRequested({
        selectedFinish,
        glossLevel: selectedGloss,
        sessionId: uploadedImage?.id,
      });
    },
    [selectedFinish, selectedGloss, uploadedImage, previewResultUrl, compositeDataUrl, beforeImagePublicUrl, tenantSlug]
  );

  // Watermark + download the preview image locally.
  const runSaveDownload = useCallback(async () => {
    const url = previewResultUrl || compositeDataUrl;
    if (!url) return;

    const isDefaultTenant = tenantSlug === 'default';
    // Strip protocol for a clean watermark label (e.g. "www.miraclemethods.com")
    // Fall back to businessName then slug so the watermark is never blank for a tenant
    const cleanWebsite = website.replace(/^https?:\/\//i, '');
    const centerText = cleanWebsite || businessName || tenantSlug;
    const watermarkOptions = isDefaultTenant
      ? { phone, email: quoteRecipientEmail }
      : { centerText, leftLabel: businessName || tenantSlug, rightLabel: 'GarageVision', phone, email: quoteRecipientEmail };

    try {
      const watermarkedUrl = await addWatermark(url, watermarkOptions);
      const link = document.createElement('a');
      link.href = watermarkedUrl;
      link.download = `garagevision-${selectedFinish.id}-${selectedGloss}.jpg`;
      link.click();
      showToast('Photo saved!', 'success');
    } catch {
      const link = document.createElement('a');
      link.href = url;
      link.download = `garagevision-${selectedFinish.id}-${selectedGloss}.jpg`;
      link.click();
      showToast('Photo saved!', 'success');
    }
  }, [compositeDataUrl, previewResultUrl, selectedFinish, selectedGloss, website, businessName, tenantSlug, phone, quoteRecipientEmail]);

  const handleSave = useCallback(async () => {
    const url = previewResultUrl || compositeDataUrl;
    if (!url) return;

    // Gate the save behind contact info — same requirement as Quote.
    if (!hasContactInfo) {
      setPendingAction('save');
      setShowQuoteModal(true);
      return;
    }

    setIsSaving(true);
    try {
      await sendQuote({ name: userName!, phone: userPhone!, email: userEmail!, zipCode: userZip! });
      await runSaveDownload();
    } catch {
      showToast('Failed to save. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [previewResultUrl, compositeDataUrl, hasContactInfo, sendQuote, runSaveDownload, userName, userPhone, userEmail, userZip]);

  const handleQuote = useCallback(async () => {
    if (!hasContactInfo) {
      setPendingAction('quote');
      setShowQuoteModal(true);
      return;
    }

    try {
      await sendQuote({ name: userName!, phone: userPhone!, email: userEmail!, zipCode: userZip! });
      showToast(`Estimate request sent! Someone from ${businessName} will contact you with a free estimate within 24 hours.`, 'success');
    } catch {
      showToast('Failed to send request. Please try again.', 'error');
    }
  }, [hasContactInfo, sendQuote, userName, userPhone, userEmail, userZip, businessName]);

  // Shared modal submit: persist contact info, then run the action that opened it.
  const handleContactSubmit = useCallback(
    async (data: { name: string; phone: string; email: string; zipCode: string }) => {
      setUserContact(data);
      await sendQuote(data);
      if (pendingAction === 'save') {
        await runSaveDownload();
      } else {
        showToast(`Estimate request sent! Someone from ${businessName} will contact you with a free estimate within 24 hours.`, 'success');
      }
    },
    [setUserContact, sendQuote, runSaveDownload, pendingAction, businessName]
  );

  useEffect(() => {
    reportFinishSelected(selectedFinish.id, selectedGloss);
  }, [selectedFinish, selectedGloss]);

  const resultUrl = previewResultUrl || compositeDataUrl;

  useEffect(() => {
    if (!resultUrl || hasHighlightedCompareRef.current) return;
    hasHighlightedCompareRef.current = true;
    setCompareHighlighted(true);
    const t = setTimeout(() => setCompareHighlighted(false), 2400);
    return () => clearTimeout(t);
  }, [resultUrl]);

  const embedHeight = useEmbedPreviewHeight();

  return (
    <div
      className={`flex flex-col ${IS_EMBED ? '' : 'h-full'}`}
      style={embedHeight ? { height: `${embedHeight}px` } : undefined}
    >
      {/* Canvas area */}
      <div className="relative flex-1 min-h-0 bg-slate-100/50">
        <CanvasViewer isCompareMode={showBeforeAfter} />

        {/* Regenerating overlay */}
        {isRegenerating && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/40 backdrop-blur-sm">
            <div className="glass-strong rounded-2xl px-6 py-4 shadow-3d-card">
              <div className="flex items-center gap-3">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-bss-blue border-t-transparent" />
                <span className="text-sm font-semibold text-slate-700">Updating preview...</span>
              </div>
            </div>
          </div>
        )}

        {/* Before/After slider overlay */}
        {showBeforeAfter && (
          <BeforeAfterSlider ratio={beforeAfterRatio} onChange={setBeforeAfterRatio} />
        )}
      </div>

      {/* Controls */}
      <div className="border-t border-blue-100/60 bg-white/90 px-3 py-2.5 backdrop-blur-md">
        {/* Save + Compare + Quote row */}
        <div className="flex gap-2">
          <button
            onClick={handleSave}
            disabled={!resultUrl || isSaving}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition-all hover:bg-slate-50 active:translate-y-0 disabled:opacity-50"
          >
            {isSaving ? (
              <svg className="h-4 w-4 animate-spin text-slate-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M12 12.75l3.75-3.75M12 12.75l-3.75-3.75M12 12.75V3.75" />
              </svg>
            )}
            {isSaving ? 'Saving...' : 'Save'}
          </button>

          <button
            onClick={() => {
              setShowBeforeAfter((v) => {
                const next = !v;
                if (next) setBeforeAfterRatio(0.5);
                return next;
              });
            }}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold shadow-md transition-all active:translate-y-0 ${
              compareHighlighted ? 'animate-compare-attention' : ''
            } ${
              showBeforeAfter
                ? 'bg-indigo-600 text-white shadow-indigo-600/30 hover:bg-indigo-700'
                : 'bg-indigo-50 text-indigo-700 border border-indigo-200 shadow-indigo-100/50 hover:bg-indigo-100'
            }`}
          >
            <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
            {showBeforeAfter ? 'Hide' : 'Compare'}
          </button>

          <button
            onClick={handleQuote}
            className="group flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-bss-blue px-3 py-2 text-xs font-semibold text-white shadow-md shadow-blue-600/20 transition-all hover:bg-bss-blue-dark active:translate-y-0"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
            </svg>
            Quote
          </button>
        </div>

        {/* Finish & Gloss selectors */}
        <div className="mt-2">
          <GlossSelector />
        </div>
        <div className="mt-2">
          <ColorSwatches />
        </div>
      </div>

      <QuoteRequestModal
        isOpen={showQuoteModal}
        onClose={() => {
          setShowQuoteModal(false);
          setPendingAction(null);
        }}
        onSubmit={handleContactSubmit}
        defaultName={userName}
        defaultPhone={userPhone}
        defaultEmail={userEmail}
        defaultZip={userZip}
        selectedFinishName={selectedFinish.name}
        selectedGloss={selectedGloss}
        title={pendingAction === 'save' ? 'Save Your Preview' : 'Get Your Free Refinishing Estimate'}
        subtitle={
          pendingAction === 'save'
            ? 'Enter your details to download your preview. A specialist may follow up with a free estimate.'
            : undefined
        }
        ctaLabel={pendingAction === 'save' ? 'Save My Photo' : 'Send My Estimate Request'}
      />
    </div>
  );
}
